'use client';

import { BoardType, PlayerType, PositionType, AIDifficultyType } from '../types';

// Board size constant
const BOARD_SIZE = 15;

// Check if a position is valid (within board boundaries and empty)
const isValidMove = (board: BoardType, row: number, col: number): boolean => {
  return (
    row >= 0 && row < BOARD_SIZE &&
    col >= 0 && col < BOARD_SIZE &&
    board[row][col] === null
  );
};

// Get all valid moves on the board
const getValidMoves = (board: BoardType): PositionType[] => {
  const validMoves: PositionType[] = [];
  
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (board[row][col] === null) {
        validMoves.push({ row, col });
      }
    }
  }
  
  return validMoves;
};

// Easy AI: Make a random valid move
const makeRandomMove = (board: BoardType): PositionType => {
  const validMoves = getValidMoves(board);
  
  // If there are valid moves, select one randomly
  if (validMoves.length > 0) {
    const randomIndex = Math.floor(Math.random() * validMoves.length);
    return validMoves[randomIndex];
  }
  
  // Fallback (should never happen in a normal game)
  return { row: -1, col: -1 };
};

// Get adjacent positions (up to 2 cells away) that are valid
const getAdjacentValidMoves = (board: BoardType): PositionType[] => {
  const occupiedCells: PositionType[] = [];
  const adjacentMoves: PositionType[] = [];
  const checkedPositions = new Set<string>();
  
  // First find all occupied cells
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (board[row][col] !== null) {
        occupiedCells.push({ row, col });
      }
    }
  }
  
  // Then find valid moves adjacent to occupied cells
  for (const cell of occupiedCells) {
    // Check in a 5x5 area around each occupied cell
    for (let rowOffset = -2; rowOffset <= 2; rowOffset++) {
      for (let colOffset = -2; colOffset <= 2; colOffset++) {
        const newRow = cell.row + rowOffset;
        const newCol = cell.col + colOffset;
        const posKey = `${newRow},${newCol}`;
        
        if (!checkedPositions.has(posKey) && isValidMove(board, newRow, newCol)) {
          adjacentMoves.push({ row: newRow, col: newCol });
          checkedPositions.add(posKey);
        }
      }
    }
  }
  
  // If the board is empty, return the center position
  if (adjacentMoves.length === 0) {
    const center = Math.floor(BOARD_SIZE / 2);
    return [{ row: center, col: center }];
  }
  
  return adjacentMoves;
};

// Check if a move would complete a line of a certain length
const checkThreat = (
  board: BoardType, 
  player: PlayerType,
  threatLength: number
): PositionType | null => {
  const opponent = player === 'BLACK' ? 'WHITE' : 'BLACK';
  
  // Function to count consecutive stones in a direction
  const countConsecutive = (
    row: number, 
    col: number, 
    rowDir: number, 
    colDir: number, 
    checkPlayer: PlayerType
  ): number => {
    let count = 0;
    let r = row;
    let c = col;
    
    // Count in the specified direction
    for (let i = 0; i < 4; i++) {
      r += rowDir;
      c += colDir;
      
      if (r < 0 || r >= BOARD_SIZE || c < 0 || c >= BOARD_SIZE || board[r][c] !== checkPlayer) {
        break;
      }
      
      count++;
    }
    
    return count;
  };
  
  // Check for potential wins or blocks
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (board[row][col] !== null) continue;
      
      // Directions to check: horizontal, vertical, diagonal
      const directions = [
        { rowDir: 0, colDir: 1 },  // horizontal
        { rowDir: 1, colDir: 0 },  // vertical
        { rowDir: 1, colDir: 1 },  // diagonal down-right
        { rowDir: 1, colDir: -1 }, // diagonal down-left
      ];
      
      // Check each direction
      for (const { rowDir, colDir } of directions) {
        // Check for winning move (our stones)
        const countForward = countConsecutive(row, col, rowDir, colDir, player);
        const countBackward = countConsecutive(row, col, -rowDir, -colDir, player);
        
        if (countForward + countBackward >= threatLength - 1) {
          return { row, col };
        }
        
        // Check for blocking move (opponent's stones)
        const oppCountForward = countConsecutive(row, col, rowDir, colDir, opponent);
        const oppCountBackward = countConsecutive(row, col, -rowDir, -colDir, opponent);
        
        if (oppCountForward + oppCountBackward >= threatLength - 1) {
          return { row, col };
        }
      }
    }
  }
  
  return null;
};

// Simplified board evaluation for speed - focus on key patterns only
const quickEvaluateBoard = (board: BoardType, player: PlayerType): number => {
  const opponent = player === 'BLACK' ? 'WHITE' : 'BLACK';
  let score = 0;
  
  // Define pattern values - reduced for performance
  const patternValues = {
    open2: 5,     // Two in a row with open ends
    open3: 50,    // Three in a row with open ends
    open4: 500,   // Four in a row with open ends
    five: 10000,  // Five in a row (win)
  };
  
  // Count stone arrangements for both players
  const patterns = {
    [player]: { open2: 0, open3: 0, open4: 0, five: 0 },
    [opponent]: { open2: 0, open3: 0, open4: 0, five: 0 }
  };
  
  // Directions to check
  const directions = [
    { rowDir: 0, colDir: 1 },  // horizontal
    { rowDir: 1, colDir: 0 },  // vertical
    { rowDir: 1, colDir: 1 },  // diagonal down-right
    { rowDir: 1, colDir: -1 }, // diagonal down-left
  ];

  // Check for winning patterns and threats
  for (let row = 0; row < BOARD_SIZE; row++) {
    for (let col = 0; col < BOARD_SIZE; col++) {
      if (board[row][col] === null) continue;
      
      const currentPlayer = board[row][col] as PlayerType;
      
      // Check each direction
      for (const { rowDir, colDir } of directions) {
        // Skip if this is not the start of a pattern
        if (
          row - rowDir >= 0 && row - rowDir < BOARD_SIZE &&
          col - colDir >= 0 && col - colDir < BOARD_SIZE &&
          board[row - rowDir][col - colDir] === currentPlayer
        ) {
          continue;
        }
        
        // Count consecutive stones
        let count = 1;
        let r = row + rowDir;
        let c = col + colDir;
        
        while (r >= 0 && r < BOARD_SIZE && c >= 0 && c < BOARD_SIZE && board[r][c] === currentPlayer) {
          count++;
          r += rowDir;
          c += colDir;
          if (count >= 5) break; // No need to count beyond 5
        }
        
        // Check if forward end is open
        const forwardOpen = r >= 0 && r < BOARD_SIZE && c >= 0 && c < BOARD_SIZE && board[r][c] === null;
        
        // Check if backward end is open
        const r2 = row - rowDir;
        const c2 = col - colDir;
        const backwardOpen = r2 >= 0 && r2 < BOARD_SIZE && c2 >= 0 && c2 < BOARD_SIZE && board[r2][c2] === null;
        
        const openEnds = (forwardOpen ? 1 : 0) + (backwardOpen ? 1 : 0);
        
        // Update pattern counts
        if (count >= 5) {
          patterns[currentPlayer].five++;
        } else if (count === 4 && openEnds > 0) {
          patterns[currentPlayer].open4++;
        } else if (count === 3 && openEnds === 2) {
          patterns[currentPlayer].open3++;
        } else if (count === 2 && openEnds === 2) {
          patterns[currentPlayer].open2++;
        }
      }
    }
  }
  
  // Calculate score based on patterns
  score = 
    patterns[player].five * patternValues.five - 
    patterns[opponent].five * patternValues.five +
    patterns[player].open4 * patternValues.open4 - 
    patterns[opponent].open4 * patternValues.open4 +
    patterns[player].open3 * patternValues.open3 - 
    patterns[opponent].open3 * patternValues.open3 +
    patterns[player].open2 * patternValues.open2 - 
    patterns[opponent].open2 * patternValues.open2;
  
  return score;
};

// Optimized minimax for Hard AI (limited search space)
const minimax = (
  board: BoardType,
  depth: number,
  alpha: number,
  beta: number,
  isMaximizingPlayer: boolean,
  player: PlayerType,
  opponent: PlayerType,
  timeLimit: number,
  startTime: number
): { score: number; position?: PositionType; timeOut: boolean } => {
  // Check for timeout - limit calculation time
  if (Date.now() - startTime > timeLimit) {
    return { score: 0, timeOut: true };
  }
  
  // Use a limited set of valid moves for better performance
  const validMoves = getAdjacentValidMoves(board);
  
  // Base case - terminal node or maximum depth reached
  if (depth === 0 || validMoves.length === 0) {
    return { score: quickEvaluateBoard(board, player), timeOut: false };
  }
  
  // Maximizing player (AI)
  if (isMaximizingPlayer) {
    let bestScore = -Infinity;
    let bestMove: PositionType | undefined;
    
    // Order moves by quick evaluation to improve pruning
    const scoredMoves = validMoves.map(move => {
      const newBoard = JSON.parse(JSON.stringify(board)) as BoardType;
      newBoard[move.row][move.col] = player;
      const score = quickEvaluateBoard(newBoard, player);
      return { move, score };
    });
    
    // Sort moves by score (descending)
    scoredMoves.sort((a, b) => b.score - a.score);
    
    // Explore moves in order of potential
    for (const { move } of scoredMoves) {
      // Make move
      const newBoard = JSON.parse(JSON.stringify(board)) as BoardType;
      newBoard[move.row][move.col] = player;
      
      // Recursively evaluate
      const result = minimax(newBoard, depth - 1, alpha, beta, false, player, opponent, timeLimit, startTime);
      
      // Check for timeout
      if (result.timeOut) {
        return { score: bestScore, position: bestMove, timeOut: true };
      }
      
      // Update best score and move
      if (result.score > bestScore) {
        bestScore = result.score;
        bestMove = move;
      }
      
      // Alpha-beta pruning
      alpha = Math.max(alpha, bestScore);
      if (beta <= alpha) break;
    }
    
    return { score: bestScore, position: bestMove, timeOut: false };
  } 
  // Minimizing player (opponent)
  else {
    let bestScore = Infinity;
    let bestMove: PositionType | undefined;
    
    // Order moves by quick evaluation to improve pruning
    const scoredMoves = validMoves.map(move => {
      const newBoard = JSON.parse(JSON.stringify(board)) as BoardType;
      newBoard[move.row][move.col] = opponent;
      const score = quickEvaluateBoard(newBoard, player); // Note: we evaluate from AI's perspective
      return { move, score };
    });
    
    // Sort moves by score (ascending for minimizer)
    scoredMoves.sort((a, b) => a.score - b.score);
    
    // Explore moves in order of potential
    for (const { move } of scoredMoves) {
      // Make move
      const newBoard = JSON.parse(JSON.stringify(board)) as BoardType;
      newBoard[move.row][move.col] = opponent;
      
      // Recursively evaluate
      const result = minimax(newBoard, depth - 1, alpha, beta, true, player, opponent, timeLimit, startTime);
      
      // Check for timeout
      if (result.timeOut) {
        return { score: bestScore, position: bestMove, timeOut: true };
      }
      
      // Update best score and move
      if (result.score < bestScore) {
        bestScore = result.score;
        bestMove = move;
      }
      
      // Alpha-beta pruning
      beta = Math.min(beta, bestScore);
      if (beta <= alpha) break;
    }
    
    return { score: bestScore, position: bestMove, timeOut: false };
  }
};

// Medium difficulty AI - block threats and make basic strategic moves
const makeMediumMove = (board: BoardType, aiPlayer: PlayerType): PositionType => {
  // Check for immediate win (4 in a row)
  const winningMove = checkThreat(board, aiPlayer, 5);
  if (winningMove) return winningMove;
  
  // Check for immediate block (opponent's 4 in a row)
  const opponentPlayer = aiPlayer === 'BLACK' ? 'WHITE' : 'BLACK';
  const blockingMove = checkThreat(board, opponentPlayer, 5);
  if (blockingMove) return blockingMove;
  
  // Check for creating a threat (3 in a row)
  const threatMove = checkThreat(board, aiPlayer, 4);
  if (threatMove) return threatMove;
  
  // Check for blocking opponent's threat (3 in a row)
  const blockThreatMove = checkThreat(board, opponentPlayer, 4);
  if (blockThreatMove) return blockThreatMove;
  
  // If no threats, make a move near existing stones
  const adjacentMoves = getAdjacentValidMoves(board);
  if (adjacentMoves.length > 0) {
    return adjacentMoves[Math.floor(Math.random() * adjacentMoves.length)];
  }
  
  // Fallback to random move (shouldn't happen with adjacentMoves logic)
  return makeRandomMove(board);
};

// Improved hard difficulty AI with performance optimizations
const makeHardMove = (board: BoardType, aiPlayer: PlayerType): PositionType => {
  const opponentPlayer = aiPlayer === 'BLACK' ? 'WHITE' : 'BLACK';
  
  // Check for immediate win (4 in a row)
  const winningMove = checkThreat(board, aiPlayer, 5);
  if (winningMove) return winningMove;
  
  // Check for immediate block (opponent's 4 in a row)
  const blockingMove = checkThreat(board, opponentPlayer, 5);
  if (blockingMove) return blockingMove;
  
  // Implement iterative deepening with time limit
  const timeLimit = 2000; // 2 second time limit
  const startTime = Date.now();
  let bestMove: PositionType | undefined;
  
  // Use iterative deepening up to depth 3
  for (let depth = 1; depth <= 2; depth++) {
    // Run minimax with current depth
    const result = minimax(
      board,
      depth,
      -Infinity,
      Infinity,
      true,
      aiPlayer,
      opponentPlayer,
      timeLimit,
      startTime
    );
    
    // If we found a move and didn't time out, update best move
    if (result.position && !result.timeOut) {
      bestMove = result.position;
    }
    
    // If we timed out, stop deepening
    if (result.timeOut) {
      console.log(`AI search timed out at depth ${depth}`);
      break;
    }
  }
  
  // If we found a move with minimax, return it
  if (bestMove) {
    return bestMove;
  }
  
  // Fallback to medium strategy if minimax didn't produce a move
  console.log('Falling back to medium strategy');
  return makeMediumMove(board, aiPlayer);
};

// Main function to get AI move based on difficulty
export const getAIMove = (
  board: BoardType, 
  difficulty: AIDifficultyType, 
  aiPlayer: PlayerType
): PositionType => {
  // Different strategy based on difficulty level
  switch (difficulty) {
    case 'easy':
      return makeRandomMove(board);
    
    case 'medium':
      return makeMediumMove(board, aiPlayer);
    
    case 'hard':
      return makeHardMove(board, aiPlayer);
    
    default:
      return makeRandomMove(board);
  }
};